<template>
  <span class="fg-help" v-if="entry">
    <!-- Help trigger + popover -->
    <v-menu
      offset-y
      :close-on-content-click="false"
      max-width="340"
      content-class="fg-help-menu"
      transition="slide-y-transition"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          type="button"
          class="fg-help__icon"
          v-on="on"
          v-bind="attrs"
          @click.stop
          :aria-label="`Help for ${entry.label}`"
        >
          <v-icon size="14">mdi-help-circle-outline</v-icon>
        </button>
      </template>

      <div class="fg-pop">
        <div class="fg-pop__title">{{ entry.label }}</div>

        <div class="fg-pop__section">
          <div class="fg-pop__label">Purpose</div>
          <div class="fg-pop__text">{{ entry.purpose }}</div>
        </div>

        <div class="fg-pop__section" v-if="entry.example">
          <div class="fg-pop__label">Example</div>
          <code class="fg-pop__example">{{ entry.example }}</code>
        </div>

        <div
          class="fg-pop__section"
          v-if="entry.displays && entry.displays.length"
        >
          <div class="fg-pop__label">Where it displays</div>
          <div class="fg-pop__thumbs">
            <button
              v-for="(d, i) in entry.displays"
              :key="i"
              type="button"
              class="fg-pop__thumb"
              @click="openLightbox(i)"
            >
              <div class="fg-pop__thumb-img-wrap">
                <img :src="d.img" :alt="d.caption" />
              </div>
              <span class="fg-pop__thumb-caption">{{ d.caption }}</span>
            </button>
          </div>
        </div>
      </div>
    </v-menu>

    <!-- Lightbox dialog -->
    <v-dialog
      v-model="lightboxOpen"
      max-width="960"
      content-class="fg-lightbox-dialog"
      @keydown.left="prev"
      @keydown.right="next"
    >
      <div class="fg-lightbox" v-if="currentDisplay">
        <div class="fg-lightbox__header">
          <div class="fg-lightbox__heading">
            <div class="fg-lightbox__title">{{ entry.label }}</div>
            <div class="fg-lightbox__caption">
              {{ currentDisplay.caption }}
            </div>
          </div>
          <button
            type="button"
            class="fg-lightbox__close"
            @click="lightboxOpen = false"
            aria-label="Close"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </div>

        <div class="fg-lightbox__stage">
          <div class="fg-lightbox__frame">
            <img
              :src="currentDisplay.img"
              :alt="currentDisplay.caption"
              class="fg-lightbox__img"
              @load="onImgLoad"
            />
            <div
              v-if="hotspotStyle"
              class="fg-lightbox__hotspot"
              :style="hotspotStyle"
            ></div>
          </div>
        </div>

        <div
          class="fg-lightbox__footer"
          v-if="entry.displays.length > 1"
        >
          <button
            type="button"
            class="fg-lightbox__nav"
            @click="prev"
            :disabled="currentIndex === 0"
          >
            <v-icon size="16">mdi-chevron-left</v-icon>
            Prev
          </button>
          <div class="fg-lightbox__counter">
            {{ currentIndex + 1 }} / {{ entry.displays.length }}
          </div>
          <button
            type="button"
            class="fg-lightbox__nav"
            @click="next"
            :disabled="currentIndex === entry.displays.length - 1"
          >
            Next
            <v-icon size="16">mdi-chevron-right</v-icon>
          </button>
        </div>
      </div>
    </v-dialog>
  </span>
</template>

<script>
import { getFieldGuide } from "./fieldGuide";

export default {
  name: "FieldGuideHelp",
  props: {
    fieldKey: { type: String, required: true },
  },
  data() {
    return {
      lightboxOpen: false,
      currentIndex: 0,
      naturalSize: { w: 0, h: 0 },
    };
  },
  computed: {
    entry() {
      return getFieldGuide(this.fieldKey);
    },
    currentDisplay() {
      if (!this.entry || !this.entry.displays) return null;
      return this.entry.displays[this.currentIndex] || null;
    },
    hotspotStyle() {
      if (!this.currentDisplay || !this.currentDisplay.hotspot) return null;
      if (!this.naturalSize.w || !this.naturalSize.h) return null;
      const { x, y, w, h } = this.currentDisplay.hotspot;
      return {
        left: (x / this.naturalSize.w) * 100 + "%",
        top: (y / this.naturalSize.h) * 100 + "%",
        width: (w / this.naturalSize.w) * 100 + "%",
        height: (h / this.naturalSize.h) * 100 + "%",
      };
    },
  },
  watch: {
    currentIndex() {
      this.naturalSize = { w: 0, h: 0 };
    },
    lightboxOpen(open) {
      if (!open) this.naturalSize = { w: 0, h: 0 };
    },
  },
  methods: {
    openLightbox(i) {
      this.currentIndex = i;
      this.lightboxOpen = true;
    },
    onImgLoad(e) {
      this.naturalSize = {
        w: e.target.naturalWidth,
        h: e.target.naturalHeight,
      };
    },
    prev() {
      if (this.currentIndex > 0) this.currentIndex--;
    },
    next() {
      if (
        this.entry &&
        this.entry.displays &&
        this.currentIndex < this.entry.displays.length - 1
      ) {
        this.currentIndex++;
      }
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .fg-help__icon {
  color: var(--app-text-faint);
}

.dark-mode .fg-pop {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .fg-pop__title {
  color: var(--app-text);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .fg-pop__text {
  color: var(--app-text);
}

.dark-mode .fg-pop__example {
  color: var(--app-text);
  background: var(--app-elevated);
  border: 1px solid var(--app-border);
}

.dark-mode .fg-pop__thumb {
  background: var(--app-surface);
  border: 1px solid var(--app-border);
}

.dark-mode .fg-pop__thumb-img-wrap {
  background: var(--app-card);
  border: 1px solid var(--app-border);
}

.dark-mode .fg-pop__thumb-caption {
  color: var(--app-neutral-fg);
}

.dark-mode .fg-lightbox {
  background: var(--app-card);
}

.dark-mode .fg-lightbox__header {
  background: linear-gradient(135deg, var(--app-surface) 0%, var(--app-accent-soft) 100%);
  border-bottom: 1px solid var(--app-border);
}

.dark-mode .fg-lightbox__title {
  color: var(--app-text);
}

.dark-mode .fg-lightbox__caption {
  color: var(--app-text-muted);
}

.dark-mode .fg-lightbox__close {
  border: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .fg-lightbox__close:hover {
  background: var(--app-danger-bg);
}

.dark-mode .fg-lightbox__stage {
  background: var(--app-elevated);
}

.dark-mode .fg-lightbox__footer {
  border-top: 1px solid var(--app-border);
  background: var(--app-surface);
}

.dark-mode .fg-lightbox__nav {
  border: 1px solid var(--app-border);
  background: var(--app-card);
  color: var(--app-neutral-fg);
}

.dark-mode .fg-lightbox__counter {
  color: var(--app-text-muted);
}

/* >>> end auto-dark <<< */

.fg-help {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  vertical-align: middle;
}

.fg-help__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;
}

.fg-help__icon:hover {
  background: #f0f7ff;
  color: #0ea5e9;
}

.fg-help__icon .v-icon {
  color: inherit !important;
}

/* Popover */
.fg-pop {
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  box-shadow:
    0 12px 24px -8px rgba(15, 23, 42, 0.1),
    0 4px 8px -4px rgba(15, 23, 42, 0.05);
  border: 1px solid #e2e8f0;
}

.fg-pop__title {
  font-size: 0.9rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
}

.fg-pop__section {
  margin-bottom: 12px;
}

.fg-pop__section:last-child {
  margin-bottom: 0;
}

.fg-pop__label {
  font-size: 0.68rem;
  font-weight: 800;
  color: #0ea5e9;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  margin-bottom: 4px;
}

.fg-pop__text {
  font-size: 0.8rem;
  color: #334155;
  line-height: 1.5;
  font-weight: 500;
}

.fg-pop__example {
  display: inline-block;
  font-family: 'SF Mono', 'Fira Code', 'Consolas', monospace;
  font-size: 0.74rem;
  color: #0f172a;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 8px;
  font-weight: 600;
}

.fg-pop__thumbs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.fg-pop__thumb {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  padding: 6px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
  font-family: inherit;
}

.fg-pop__thumb:hover {
  border-color: #0ea5e9;
  background: #f0f7ff;
  transform: translateY(-1px);
}

.fg-pop__thumb-img-wrap {
  width: 100%;
  aspect-ratio: 16 / 10;
  background: #fff;
  border-radius: 5px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.fg-pop__thumb-img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.fg-pop__thumb-caption {
  font-size: 0.7rem;
  font-weight: 600;
  color: #475569;
  line-height: 1.3;
}

/* Lightbox */
.fg-lightbox {
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

.fg-lightbox__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f0f7ff 100%);
  border-bottom: 1px solid #e2e8f0;
  flex: 0 0 auto;
}

.fg-lightbox__title {
  font-size: 1rem;
  font-weight: 800;
  color: #0f172a;
}

.fg-lightbox__caption {
  font-size: 0.78rem;
  color: #64748b;
  font-weight: 500;
  margin-top: 2px;
}

.fg-lightbox__close {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.fg-lightbox__close:hover {
  background: #fef2f2;
  border-color: #fca5a5;
}

.fg-lightbox__stage {
  background: #f1f5f9;
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.fg-lightbox__frame {
  position: relative;
  display: inline-block;
  max-width: 100%;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px -4px rgba(15, 23, 42, 0.1);
}

.fg-lightbox__img {
  display: block;
  max-width: 100%;
  height: auto;
}

.fg-lightbox__hotspot {
  position: absolute;
  border: 3px solid #ef4444;
  border-radius: 4px;
  box-shadow:
    0 0 0 9999px rgba(15, 23, 42, 0.4),
    0 0 16px rgba(239, 68, 68, 0.6);
  pointer-events: none;
  z-index: 2;
  animation: fg-pulse 1.6s ease-in-out infinite;
}

@keyframes fg-pulse {
  0%, 100% {
    box-shadow:
      0 0 0 9999px rgba(15, 23, 42, 0.4),
      0 0 16px rgba(239, 68, 68, 0.6);
  }
  50% {
    box-shadow:
      0 0 0 9999px rgba(15, 23, 42, 0.4),
      0 0 24px rgba(239, 68, 68, 0.9);
  }
}

.fg-lightbox__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
  flex: 0 0 auto;
}

.fg-lightbox__nav {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s ease;
}

.fg-lightbox__nav:hover:not(:disabled) {
  border-color: #0ea5e9;
  color: #0ea5e9;
  background: #f0f7ff;
}

.fg-lightbox__nav:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.fg-lightbox__counter {
  font-size: 0.78rem;
  font-weight: 700;
  color: #64748b;
}
</style>

<style>
/* Unscoped overrides for v-menu / v-dialog content */
.fg-help-menu {
  border-radius: 12px !important;
  box-shadow: none !important;
  background: transparent !important;
  overflow: visible !important;
}

.fg-lightbox-dialog {
  border-radius: 16px !important;
  box-shadow:
    0 24px 48px -12px rgba(15, 23, 42, 0.25),
    0 8px 16px -8px rgba(15, 23, 42, 0.1) !important;
  overflow: hidden !important;
}
</style>
